import { Theme } from "@emotion/react";

const UTTheme: Theme = {
  palette: {
    primary: {
      light: "#ef2092",
      main: "#cf0072",
      dark: "#b3004b",
    },
    secondary: {
      light: "#00675a",
      main: "#00675a",
      dark: "#004b48",
    },
    purple: {
      light: "#5F3D8F",
      main: "#4F2D7F",
      dark: "#3F1D6F",
    },
    green: {
      lightest: "rgba(1, 103, 90, 0.2)",
      lighter: "rgba(1, 103, 90, 0.4)",
      light: "rgba(1, 103, 90, 0.6)",
      main: "#01675A",
      dark: "#00574A",
    },
    lightblue: {
      light: "#73C1F5",
      main: "#63B1E5",
      dark: "#53A1D5",
    },
    hold: {
      light: "#375AC0",
      main: "#173AA0",
      dark: "#072A90",
    },
    error: {
      light: "#cd202c",
      main: "#cd202c",
      dark: "#b9121a",
    },
    warning: {
      light: "#ec7a08",
      main: "#ec7a08",
      dark: "#e35a04",
      contrastText: "#FFF",
    },
    info: {
      light: "#0093b4",
      main: "#0093b4",
      dark: "#006c8f",
    },
    succes: {
      light: "#3f9c35",
      main: "#3f9c35",
      dark: "#287c31",
    },
    dark: {
      lightest: "#909090",
      light: "#707070",
      main: "#1D2327",
      dark: "#000000",
      contrastText: "#FFF",
    },
    light: {
      light: "#FFF",
      main: "#F0F1F2",
      dark: "#E8E8E8",
      tintedBackground: "#dfe7e7",
    },
  },
  typography: {
    fontFamily: "Univers",
    h1: {
      fontSize: "70px",
      textTransform: "uppercase",
      fontWeight: 700,
      fontFamily: "Univers Cn",
      lineHeight: "65px",
    },
    h2: {
      fontSize: "2rem",
      fontWeight: 700,
      textTransform: "uppercase",
      fontFamily: "Univers Cn",
    },
    h3: {
      fontSize: "1.625rem",
      fontWeight: 700,
      textTransform: "uppercase",
      fontFamily: "Univers Cn",
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: 700,
      textTransform: "uppercase",
      fontFamily: "Univers Cn",
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 700,
      textTransform: "uppercase",
      fontFamily: "Univers Cn",
    },
    subtitle1: {
      fontSize: "15px",
      fontWeight: 700,
      fontFamily: "Univers",
      lineHeight: "normal",
    },
    caption: {
      fontWeight: 300,
      color: "#1D2327",
      fontFamily: "Univers Cn",
      fontSize: "1rem",
    },
    overline: {
      fontFamily: "Univers Cn",
      fontWeight: 300,
      fontSize: ".8rem",
    },
  },
  components: {
    MuiCardContent: {
      styleOverrides: {
        root: {
          paddingTop: "0",
          paddingBottom: "0",
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          minWidth: "1em",
          minHeight: "1em",
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          padding: "0px",
          border: "unset",
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          border: "unset",
          backgroundColor: "unset",
          borderRadius: "0px",
          "&:hover": {
            backgroundColor: "unset",
          },
        },
      },
    },
    MuiRating: {
      styleOverrides: {
        root: {
          gap: "2px",
          fontSize: "1.4rem",
          "& .MuiRating-iconFilled": {
            color: "#EC7A08",
          },
          "& .MuiRating-icon": {
            color: "#EC7A08",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "0",
          boxShadow: "0px 4px 10px 0px rgba(0, 0, 0, 0.10)",
          paddingLeft: 24,
          paddingRight: 24,
          paddingTop: 32,
          paddingBottom: 20,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          border: "none",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          paddingTop: ".5rem",
          paddingBottom: ".5rem",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          fontFamily: "Univers Cn",
          boxShadow: "unset",
          fontWeight: 700,
          paddingLeft: "1.75em",
          paddingRight: "1.75em",
          lineHeight: 2,
          paddingTop: 8,
          paddingBottom: 8,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          position: "relative",
        },
      },
    },
    MuiDialogPaper: {
      styleOverrides: {
        root: {
          position: "relative",
          bottom: "auto",
          width: "62%",
          marginRight: "140px",
          marginLeft: "100px",
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(232,232,232,0.95)",
          boxShadow: "0px 15px 15px rgba(0,0,0,0.11)",
          top: "0",
          bottom: "0",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#FFF",
          color: "#1D2327",
          filter: "drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.10))",
        },
        arrow: {
          borderColor: "#FFF",
          color: "#FFF",
        },
      },
    },
  },
};

export default UTTheme;
